export const DEFAULT_API_BASE = "https://api.fileagent.app";
export const DEFAULT_SITE_URL = "https://fileagent.app";
export const DEFAULT_SUPPORT_EMAIL = "support@fileagent.app";
export const TERMS_VERSION = "2026-06-24";
export const LEGAL_EFFECTIVE_DATE = "June 24, 2026";
/** Privacy Policy revision date. */
export const PRIVACY_UPDATED_DATE = "October 28, 2026";

function envOr(key: string, fallback: string): string {
  const value = (import.meta.env?.[key] as string | undefined) ?? "";
  return value.trim() || fallback;
}

export const siteConfig = {
  siteUrl: envOr("VITE_FILEAGENT_SITE_URL", DEFAULT_SITE_URL).replace(/\/$/, ""),
  docsUrl: envOr("VITE_FILEAGENT_DOCS_URL", "https://docs.fileagent.app").replace(/\/$/, ""),
  apiBaseUrl: envOr("VITE_FILEAGENT_API_BASE_URL", DEFAULT_API_BASE).replace(/\/$/, ""),
  publicConfigUrl:
    envOr("VITE_FILEAGENT_PUBLIC_CONFIG_URL", "") ||
    `${envOr("VITE_FILEAGENT_API_BASE_URL", DEFAULT_API_BASE).replace(/\/$/, "")}/fileagent/public-config`,
  licenseLookupPath: envOr("VITE_FILEAGENT_LICENSE_LOOKUP_PATH", "/fileagent/license/checkout-session"),
  // TODO: Set VITE_FILEAGENT_DOWNLOAD_URL to the signed/package URL when the distributable is uploaded.
  downloadUrl: envOr("VITE_FILEAGENT_DOWNLOAD_URL", ""),
  windowsDownloadUrl: envOr("VITE_FILEAGENT_WINDOWS_DOWNLOAD_URL", ""),
  linuxDebDownloadUrl: envOr("VITE_FILEAGENT_LINUX_DEB_DOWNLOAD_URL", ""),
  supportEmail: envOr("VITE_FILEAGENT_SUPPORT_EMAIL", DEFAULT_SUPPORT_EMAIL)
} as const;

export type DownloadPlatform = "linuxDeb" | "windows";

export const DOWNLOADS: Record<DownloadPlatform, {
  label: string;
  platformLabel: string;
  fileType: string;
  extension: string;
  envKey: string;
  fallbackPath: string;
}> = {
  linuxDeb: {
    label: "Linux .deb Package",
    platformLabel: "Linux",
    fileType: "Debian package",
    extension: ".deb",
    envKey: "VITE_FILEAGENT_LINUX_DEB_DOWNLOAD_URL",
    // Static last-resort URL used only when the live manifest and env URLs are unavailable.
    fallbackPath: "/downloads/releases/2.4.0/fileagent_2.4.0_amd64.deb"
  },
  windows: {
    label: "Windows EXE Installer",
    platformLabel: "Windows",
    fileType: "EXE installer",
    extension: ".exe",
    envKey: "VITE_FILEAGENT_WINDOWS_DOWNLOAD_URL",
    fallbackPath: "/downloads/releases/2.4.0/FileAgent_2.4.0_Windows-x64-Setup.exe"
  }
};

export interface DownloadResolveOptions {
  /** URL resolved from the live `/downloads/latest.json` manifest (preferred). */
  manifestUrl?: string;
  /** Legacy per-purchase/license download URL (kept for backward compatibility). */
  publicDownloadUrl?: string;
}

/**
 * Resolve the download URL for a platform. Priority:
 *   1. live manifest URL (latest.json — single source of truth)
 *   2. build-time env URL (VITE_FILEAGENT_*_DOWNLOAD_URL)
 *   3. legacy license/public download URL (windows/exe only, as before)
 *   4. static fallbackPath bundled with the site
 */
export function downloadUrlFor(platform: DownloadPlatform, options: DownloadResolveOptions = {}): string {
  const { manifestUrl, publicDownloadUrl } = options;
  if (manifestUrl) return manifestUrl;

  const packageLikePublicUrl = publicDownloadUrl && /\.(exe|zip)(\?|#|$)/i.test(publicDownloadUrl)
    ? publicDownloadUrl
    : "";
  const packageLikeConfiguredUrl = /\.(exe|zip)(\?|#|$)/i.test(siteConfig.downloadUrl)
    ? siteConfig.downloadUrl
    : "";
  if (platform === "windows") {
    return siteConfig.windowsDownloadUrl || packageLikePublicUrl || packageLikeConfiguredUrl || DOWNLOADS.windows.fallbackPath;
  }
  return siteConfig.linuxDebDownloadUrl || DOWNLOADS.linuxDeb.fallbackPath;
}

export function absoluteSiteUrl(path = "/"): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${siteConfig.siteUrl}${cleanPath}`;
}
